import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../config';
import { ArrowLeft, DollarSign, Edit, Plus, Printer, Save, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { writeSanitizedHTML } from '../utils/safePrint';

const API = `${API_BASE_URL}/api`;

const CostUnitForm = ({ currentExercise, theme }) => {
  const API = API_BASE_URL + '/api';
  const [view, setView] = useState('list');
  const [entries, setEntries] = useState([]);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({
    exercise_name: currentExercise?.exercise_name || '',
    incident_name: '',
    operational_period_from: '',
    operational_period_to: '',
    cost_unit_leader: '',
    contact_info: '',
    personnel_costs: [],
    equipment_costs: [],
    procurement_contracts: [],
    incident_costs: [],
    fuel_costs: [],
    total_personnel_cost: '',
    total_equipment_cost: '',
    total_procurement_cost: '',
    total_incident_cost: '',
    total_fuel_cost: '',
    grand_total_cost: '',
    cost_estimate_notes: '',
    potential_savings_notes: '',
    prepared_by: '',
    date_prepared: ''
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'saved', 'saving', 'error'
  const [lastSynced, setLastSynced] = useState(null);

  // Fetch entries
  const fetchEntries = async (silent = false) => {
    if (!currentExercise?.id) return;
    
    if (!silent) setLoading(true);
    try {
      const response = await fetch(`${API}/cost-unit?exercise_id=${currentExercise.id}`);
      if (response.ok) {
        const data = await response.json();
        setEntries(data);
        setLastSynced(new Date());
      }
    } catch (error) {
      if (!silent) console.error('Error fetching Cost Unit entries:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, [currentExercise?.id]);

  // Setup polling for real-time sync (every 30 seconds)
  useEffect(() => {
    if (currentExercise?.id) {
      const pollInterval = setInterval(() => {
        fetchEntries(true);
      }, 30000);
      
      return () => clearInterval(pollInterval);
    }
  }, [currentExercise?.id]);

  // Initialize form with editing data
  useEffect(() => {
    if (editingEntry) {
      setFormData({
        exercise_name: editingEntry.exercise_name || currentExercise?.exercise_name || '',
        incident_name: editingEntry.incident_name || '',
        operational_period_from: editingEntry.operational_period_from || '',
        operational_period_to: editingEntry.operational_period_to || '',
        cost_unit_leader: editingEntry.cost_unit_leader || '',
        contact_info: editingEntry.contact_info || '',
        personnel_costs: editingEntry.personnel_costs || [],
        equipment_costs: editingEntry.equipment_costs || [],
        procurement_contracts: editingEntry.procurement_contracts || [],
        incident_costs: editingEntry.incident_costs || [],
        fuel_costs: editingEntry.fuel_costs || [],
        total_personnel_cost: editingEntry.total_personnel_cost || '',
        total_equipment_cost: editingEntry.total_equipment_cost || '',
        total_procurement_cost: editingEntry.total_procurement_cost || '',
        total_incident_cost: editingEntry.total_incident_cost || '',
        total_fuel_cost: editingEntry.total_fuel_cost || '',
        grand_total_cost: editingEntry.grand_total_cost || '',
        cost_estimate_notes: editingEntry.cost_estimate_notes || '',
        potential_savings_notes: editingEntry.potential_savings_notes || '',
        prepared_by: editingEntry.prepared_by || '',
        date_prepared: editingEntry.date_prepared || ''
      });
    } else {
      setFormData({
        exercise_name: currentExercise?.exercise_name || '',
        incident_name: '',
        operational_period_from: '',
        operational_period_to: '',
        cost_unit_leader: '',
        contact_info: '',
        personnel_costs: [],
        equipment_costs: [],
        procurement_contracts: [],
        incident_costs: [],
        fuel_costs: [],
        total_personnel_cost: '',
        total_equipment_cost: '',
        total_procurement_cost: '',
        total_incident_cost: '',
        total_fuel_cost: '',
        grand_total_cost: '',
        cost_estimate_notes: '',
        potential_savings_notes: '',
        prepared_by: '',
        date_prepared: ''
      });
    }
  }, [editingEntry, currentExercise]);

  // Add personnel cost item
  const addPersonnelCost = () => {
    const newItem = {
      id: Date.now().toString(),
      name: '',
      position: '',
      agency: '',
      start_date: '',
      end_date: '',
      hours_worked: '',
      hourly_rate: '',
      total_cost: ''
    };
    setFormData(prev => ({
      ...prev,
      personnel_costs: [...prev.personnel_costs, newItem]
    }));
  };

  // Parse currency strings (handles "$3,000.00", "1,500", "$75.00", etc.) → number
  const parseCost = (value) => {
    if (value == null || value === '') return 0;
    const n = parseFloat(String(value).replace(/[$,\s]/g, ''));
    return isNaN(n) ? 0 : n;
  };

  const updatePersonnelCost = (index, field, value) => {
    const updated = [...formData.personnel_costs];
    updated[index] = { ...updated[index], [field]: value };
    
    // Auto-calculate total if hours or rate changed
    if (field === 'hours_worked' || field === 'hourly_rate') {
      const hours = parseCost(field === 'hours_worked' ? value : updated[index].hours_worked);
      const rate = parseCost(field === 'hourly_rate' ? value : updated[index].hourly_rate);
      updated[index].total_cost = (hours * rate).toFixed(2);
    }
    
    setFormData(prev => ({ ...prev, personnel_costs: updated }));
  };

  const deletePersonnelCost = (index) => {
    setFormData(prev => ({
      ...prev,
      personnel_costs: prev.personnel_costs.filter((_, i) => i !== index)
    }));
  };

  // Calculate total personnel cost whenever personnel_costs changes
  useEffect(() => {
    const total = formData.personnel_costs.reduce((sum, item) => {
      return sum + parseCost(item.total_cost);
    }, 0);
    setFormData(prev => ({ ...prev, total_personnel_cost: total.toFixed(2) }));
  }, [formData.personnel_costs]);

  // Calculate total equipment cost whenever equipment_costs changes
  useEffect(() => {
    const total = formData.equipment_costs.reduce((sum, item) => {
      return sum + parseCost(item.total_cost);
    }, 0);
    setFormData(prev => ({ ...prev, total_equipment_cost: total.toFixed(2) }));
  }, [formData.equipment_costs]);

  // Calculate total procurement cost whenever procurement_contracts changes
  useEffect(() => {
    const total = formData.procurement_contracts.reduce((sum, item) => {
      return sum + parseCost(item.contract_value);
    }, 0);
    setFormData(prev => ({ ...prev, total_procurement_cost: total.toFixed(2) }));
  }, [formData.procurement_contracts]);

  // Calculate total incident cost whenever incident_costs changes
  useEffect(() => {
    const total = formData.incident_costs.reduce((sum, item) => {
      return sum + parseCost(item.amount);
    }, 0);
    setFormData(prev => ({ ...prev, total_incident_cost: total.toFixed(2) }));
  }, [formData.incident_costs]);

  // Calculate total fuel cost whenever fuel_costs changes
  useEffect(() => {
    const total = formData.fuel_costs.reduce((sum, item) => {
      return sum + parseCost(item.cost);
    }, 0);
    setFormData(prev => ({ ...prev, total_fuel_cost: total.toFixed(2) }));
  }, [formData.fuel_costs]);

  // Calculate grand total cost whenever any of the totals change
  useEffect(() => {
    const personnelTotal = parseCost(formData.total_personnel_cost);
    const equipmentTotal = parseCost(formData.total_equipment_cost);
    const procurementTotal = parseCost(formData.total_procurement_cost);
    const incidentTotal = parseCost(formData.total_incident_cost);
    const fuelTotal = parseCost(formData.total_fuel_cost);
    const grandTotal = personnelTotal + equipmentTotal + procurementTotal + incidentTotal + fuelTotal;
    setFormData(prev => ({ ...prev, grand_total_cost: grandTotal.toFixed(2) }));
  }, [formData.total_personnel_cost, formData.total_equipment_cost, formData.total_procurement_cost, formData.total_incident_cost, formData.total_fuel_cost]);


  // Add equipment cost item
  const addEquipmentCost = () => {
    const newItem = {
      id: Date.now().toString(),
      equipment_name: '',
      equipment_type: '',
      quantity: '',
      daily_rate: '',
      days_used: '',
      total_cost: '',
      vendor: ''
    };
    setFormData(prev => ({
      ...prev,
      equipment_costs: [...prev.equipment_costs, newItem]
    }));
  };

  const updateEquipmentCost = (index, field, value) => {
    const updated = [...formData.equipment_costs];
    updated[index] = { ...updated[index], [field]: value };
    
    // Auto-calculate total if quantity, daily_rate, or days_used changed
    if (field === 'quantity' || field === 'daily_rate' || field === 'days_used') {
      const quantity = parseCost(field === 'quantity' ? value : updated[index].quantity);
      const dailyRate = parseCost(field === 'daily_rate' ? value : updated[index].daily_rate);
      const daysUsed = parseCost(field === 'days_used' ? value : updated[index].days_used);
      updated[index].total_cost = (quantity * dailyRate * daysUsed).toFixed(2);
    }
    
    setFormData(prev => ({ ...prev, equipment_costs: updated }));
  };

  const deleteEquipmentCost = (index) => {
    setFormData(prev => ({
      ...prev,
      equipment_costs: prev.equipment_costs.filter((_, i) => i !== index)
    }));
  };

  // Add procurement contract
  const addProcurementContract = () => {
    const newItem = {
      id: Date.now().toString(),
      contract_number: '',
      vendor_name: '',
      description: '',
      contract_date: '',
      contract_value: '',
      status: 'Pending',
      payment_terms: ''
    };
    setFormData(prev => ({
      ...prev,
      procurement_contracts: [...prev.procurement_contracts, newItem]
    }));
  };

  const updateProcurementContract = (index, field, value) => {
    const updated = [...formData.procurement_contracts];
    updated[index] = { ...updated[index], [field]: value };
    setFormData(prev => ({ ...prev, procurement_contracts: updated }));
  };

  const deleteProcurementContract = (index) => {
    setFormData(prev => ({
      ...prev,
      procurement_contracts: prev.procurement_contracts.filter((_, i) => i !== index)
    }));
  };

  // Add incident cost
  const addIncidentCost = () => {
    const newItem = {
      id: Date.now().toString(),
      date: '',
      category: '',
      description: '',
      amount: '',
      approved_by: '',
      notes: ''
    };
    setFormData(prev => ({
      ...prev,
      incident_costs: [...prev.incident_costs, newItem]
    }));
  };

  const updateIncidentCost = (index, field, value) => {
    const updated = [...formData.incident_costs];
    updated[index] = { ...updated[index], [field]: value };
    setFormData(prev => ({ ...prev, incident_costs: updated }));
  };

  const deleteIncidentCost = (index) => {
    setFormData(prev => ({
      ...prev,
      incident_costs: prev.incident_costs.filter((_, i) => i !== index)
    }));
  };

  // Fuel cost handlers
  const addFuelCost = () => {
    const newItem = {
      id: Date.now().toString(),
      date: '',
      vendor: '',
      location: '',
      fuel_type: 'Regular',
      measurement: 'US Gal',
      quantity: '',
      cost: '',
      notes: ''
    };
    setFormData(prev => ({
      ...prev,
      fuel_costs: [...prev.fuel_costs, newItem]
    }));
  };

  const updateFuelCost = (index, field, value) => {
    const updated = [...formData.fuel_costs];
    updated[index] = { ...updated[index], [field]: value };
    setFormData(prev => ({ ...prev, fuel_costs: updated }));
  };

  const deleteFuelCost = (index) => {
    setFormData(prev => ({
      ...prev,
      fuel_costs: prev.fuel_costs.filter((_, i) => i !== index)
    }));
  };

  // Save entry
  const saveEntry = async () => {
    if (!currentExercise?.id) {
      alert('No exercise selected');
      return;
    }

    setLoading(true);
    setSaving(true);
    setSaveStatus('saving');
    try {
      const payload = {
        ...formData,
        exercise_id: currentExercise.id,
        exercise_name: currentExercise.exercise_name
      };

      let response;
      if (editingEntry) {
        response = await fetch(`${API}/cost-unit/${editingEntry.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else {
        response = await fetch(`${API}/cost-unit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }

      if (response.ok) {
        setSaveStatus('saved');
        setLastSynced(new Date());
        // Update editingEntry with the new/updated data
        const savedData = await response.json();
        setEditingEntry(savedData);
        fetchEntries(true);
        // Clear saved status after 3 seconds
        setTimeout(() => setSaveStatus(null), 3000);
      } else {
        setSaveStatus('error');
        alert('Failed to save entry');
      }
    } catch (error) {
      console.error('Error saving entry:', error);
      setSaveStatus('error');
      alert('Error saving entry');
    } finally {
      setLoading(false);
      setSaving(false);
    }
  };

  // Delete entry
  const deleteEntry = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;

    setLoading(true);
    try {
      const response = await fetch(`${API}/cost-unit/${entryId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        alert('Entry deleted successfully!');
        fetchEntries();
      } else {
        alert('Failed to delete entry');
      }
    } catch (error) {
      console.error('Error deleting entry:', error);
      alert('Error deleting entry');
    } finally {
      setLoading(false);
    }
  };

  // Comprehensive Print Function (DOM-safe via writeSanitizedHTML)
  const handlePrint = () => {
    const data = formData || {};
    const exerciseName = data.exercise_name || currentExercise?.exercise_name || 'N/A';
    const incidentName = data.incident_name || 'N/A';
    const generated = new Date().toLocaleString();

    const tableSection = (title, columns, rows, emptyMsg) => {
      if (!rows || rows.length === 0) {
        return `<div class="section"><h2>${title}</h2><p class="empty">${emptyMsg}</p></div>`;
      }
      const head = columns.map(c => `<th>${c.label}</th>`).join('');
      const body = rows.map(r =>
        `<tr>${columns.map(c => `<td>${r[c.key] != null && r[c.key] !== '' ? r[c.key] : ''}</td>`).join('')}</tr>`
      ).join('');
      return `
        <div class="section">
          <h2>${title} <span class="count">(${rows.length})</span></h2>
          <table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>
        </div>`;
    };

    const infoItem = (label, value) =>
      `<div class="info-item"><div class="info-label">${label}</div><div class="info-value">${value || 'N/A'}</div></div>`;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Cost Unit Report - ${exerciseName}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; line-height: 1.5; color: #111827; }
            h1 { color: #3b82f6; text-align: center; border-bottom: 3px solid #3b82f6; padding-bottom: 10px; margin-bottom: 6px; }
            .sub { text-align: center; color: #6b7280; margin-bottom: 24px; font-size: 12px; }
            h2 { color: #3b82f6; margin-top: 26px; border-bottom: 2px solid #e5e7eb; padding-bottom: 5px; font-size: 16px; }
            .count { color: #6b7280; font-size: 13px; font-weight: normal; }
            .section { margin-bottom: 22px; page-break-inside: avoid; }
            .info-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; margin: 10px 0; }
            .info-grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin: 10px 0; }
            .info-grid-1 { display: grid; grid-template-columns: 1fr; gap: 10px; margin: 10px 0; }
            .info-item { padding: 8px 10px; background: #f9fafb; border-left: 3px solid #3b82f6; }
            .info-label { font-weight: bold; color: #6b7280; font-size: 11px; text-transform: uppercase; }
            .info-value { color: #111827; margin-top: 3px; font-size: 13px; white-space: pre-wrap; }
            table { width: 100%; border-collapse: collapse; margin: 8px 0; font-size: 12px; }
            th, td { border: 1px solid #e5e7eb; padding: 6px 8px; text-align: left; vertical-align: top; }
            th { background: #eff6ff; font-weight: bold; color: #1e40af; }
            tr:nth-child(even) { background: #f9fafb; }
            .empty { color: #9ca3af; font-style: italic; font-size: 12px; }
            .grand { background: #fef3c7; border-left: 3px solid #f59e0b; }
            .footer { text-align: center; color: #6b7280; margin-top: 28px; padding-top: 12px; border-top: 1px solid #e5e7eb; font-size: 11px; }
            @media print { body { padding: 12px; } .section { page-break-inside: avoid; } }
          </style>
        </head>
        <body>
          <h1>Cost Unit Report</h1>
          <div class="sub"><strong>Exercise:</strong> ${exerciseName} &nbsp;•&nbsp; <strong>Incident:</strong> ${incidentName} &nbsp;•&nbsp; <strong>Generated:</strong> ${generated}</div>

          <div class="section">
            <h2>Basic Information</h2>
            <div class="info-grid">
              ${infoItem('Exercise Name', exerciseName)}
              ${infoItem('Incident Name', incidentName)}
              ${infoItem('Operational Period From', data.operational_period_from)}
              ${infoItem('Operational Period To', data.operational_period_to)}
              ${infoItem('Cost Unit Leader', data.cost_unit_leader)}
              ${infoItem('Contact Info', data.contact_info)}
            </div>
          </div>

          ${tableSection('Personnel Costs',
            [
              { key: 'name', label: 'Name' },
              { key: 'position', label: 'Position' },
              { key: 'agency', label: 'Agency' },
              { key: 'start_date', label: 'Start' },
              { key: 'end_date', label: 'End' },
              { key: 'hours_worked', label: 'Hours' },
              { key: 'hourly_rate', label: 'Rate ($)' },
              { key: 'total_cost', label: 'Total ($)' }
            ],
            data.personnel_costs,
            'No personnel costs recorded.')}

          ${tableSection('Equipment Costs',
            [
              { key: 'equipment_name', label: 'Equipment' },
              { key: 'equipment_type', label: 'Type' },
              { key: 'quantity', label: 'Qty' },
              { key: 'daily_rate', label: 'Daily Rate ($)' },
              { key: 'days_used', label: 'Days' },
              { key: 'total_cost', label: 'Total ($)' },
              { key: 'vendor', label: 'Vendor' }
            ],
            data.equipment_costs,
            'No equipment costs recorded.')}

          ${tableSection('Procurement Contracts',
            [
              { key: 'contract_number', label: 'Contract #' },
              { key: 'vendor_name', label: 'Vendor' },
              { key: 'description', label: 'Description' },
              { key: 'contract_date', label: 'Date' },
              { key: 'contract_value', label: 'Value ($)' },
              { key: 'status', label: 'Status' },
              { key: 'payment_terms', label: 'Payment Terms' }
            ],
            data.procurement_contracts,
            'No procurement contracts recorded.')}

          ${tableSection('Other Incident Costs',
            [
              { key: 'date', label: 'Date' },
              { key: 'category', label: 'Category' },
              { key: 'description', label: 'Description' },
              { key: 'amount', label: 'Amount ($)' },
              { key: 'approved_by', label: 'Approved By' },
              { key: 'notes', label: 'Notes' }
            ],
            data.incident_costs,
            'No incident costs recorded.')}

          ${tableSection('Fuel Costs',
            [
              { key: 'date', label: 'Date' },
              { key: 'vendor', label: 'Vendor' },
              { key: 'location', label: 'Location' },
              { key: 'fuel_type', label: 'Fuel Type' },
              { key: 'measurement', label: 'Measurement' },
              { key: 'quantity', label: 'Quantity' },
              { key: 'cost', label: 'Cost ($)' }
            ],
            data.fuel_costs,
            'No fuel costs recorded.')}

          <div class="section">
            <h2>Cost Summary</h2>
            <div class="info-grid">
              ${infoItem('Total Personnel Cost ($)', data.total_personnel_cost)}
              ${infoItem('Total Equipment Cost ($)', data.total_equipment_cost)}
              ${infoItem('Total Procurement Cost ($)', data.total_procurement_cost)}
              ${infoItem('Total Incident Cost ($)', data.total_incident_cost)}
              ${infoItem('Total Fuel Cost ($)', data.total_fuel_cost)}
            </div>
            <div class="info-grid-1">
              <div class="info-item grand">
                <div class="info-label">Grand Total Cost ($)</div>
                <div class="info-value" style="font-size:18px;font-weight:bold;color:#92400e">${data.grand_total_cost || '0.00'}</div>
              </div>
            </div>
            <div class="info-grid-1">
              ${infoItem('Cost Estimate Notes', data.cost_estimate_notes)}
              ${infoItem('Potential Savings Notes', data.potential_savings_notes)}
            </div>
            <div class="info-grid">
              ${infoItem('Prepared By', data.prepared_by)}
              ${infoItem('Date Prepared', data.date_prepared)}
            </div>
          </div>

          <div class="footer">
            <div><strong>Cost Unit Report</strong> &nbsp;|&nbsp; ${exerciseName}</div>
            <div>Powered by EXRSIM</div>
          </div>
        </body>
      </html>
    `;

    writeSanitizedHTML(printWindow.document, printContent);
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 250);
  };

  // List view
  if (view === 'list') {
    return (
      <div className="p-6">
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className={`text-3xl font-bold ${theme.colors.textPrimary} mb-2`}>Cost Unit</h1>
            <p className={`${theme.colors.textMuted}`}>Track incident costs, personnel, equipment, and procurement contracts</p>
          </div>
          <Button onClick={() => setView('form')} className="bg-orange-500 hover:bg-orange-600">
            <Plus className="h-4 w-4 mr-2" />
            New Entry
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className={`${theme.colors.textMuted}`}>Loading...</p>
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-12">
            <DollarSign className={`h-16 w-16 ${theme.colors.textMuted} mx-auto mb-4`} />
            <p className={`${theme.colors.textMuted} mb-4`}>No cost entries found</p>
            <Button onClick={() => setView('form')} className="bg-orange-500 hover:bg-orange-600">
              Create First Entry
            </Button>
          </div>
        ) : (
          <div className="grid gap-4">
            {entries.map((entry) => (
              <Card key={entry.id} className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
                <CardHeader>
                  <CardTitle className={`${theme.colors.textPrimary}`}>
                    {entry.incident_name || 'Untitled Entry'}
                  </CardTitle>
                  <CardDescription className={`${theme.colors.textMuted}`}>
                    Period: {entry.operational_period_from} - {entry.operational_period_to} | Total: ${entry.grand_total_cost || '0'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setEditingEntry(entry);
                        setView('form');
                      }}
                      className="border-blue-500 text-blue-500 hover:bg-blue-500/10"
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => deleteEntry(entry.id)}
                      className="border-red-500 text-red-500 hover:bg-red-500/10"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Form view
  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6 flex justify-between items-center print:hidden">
        <Button onClick={() => {
          setView('list');
          setEditingEntry(null);
        }} variant="outline">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to List
        </Button>
        <Button onClick={handlePrint} className="bg-blue-500 hover:bg-blue-600">
          <Printer className="h-4 w-4 mr-2" />
          Print Costs
        </Button>
      </div>

      <div className="space-y-6">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className={`text-3xl font-bold ${theme.colors.textPrimary} mb-2`}>Cost Unit Report</h1>
        </div>

        {/* Basic Information Card */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-orange-500">Basic Information</CardTitle>
            </div>
            <div className="flex items-center gap-2 print:hidden">
              {saveStatus === 'saving' && <span className="text-yellow-500 text-sm">Saving...</span>}
              {saveStatus === 'saved' && <span className="text-green-500 text-sm">✓ Saved</span>}
              {saveStatus === 'error' && <span className="text-red-500 text-sm">Save failed</span>}
              <Button onClick={saveEntry} disabled={saving} size="sm" className="bg-green-600 hover:bg-green-700">
                <Save className="h-4 w-4 mr-1" />
                Save
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Exercise Name</Label>
                <div className={`p-2 bg-gray-800 rounded border border-gray-600`}>
                  <span className={`${theme.colors.textPrimary}`}>{currentExercise?.exercise_name || 'N/A'}</span>
                </div>
              </div>
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Incident Name</Label>
                <Input
                  value={formData.incident_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, incident_name: e.target.value }))}
                  className={`bg-gray-800 border-gray-600 text-white`}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Operational Period From</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="date"
                    value={formData.operational_period_from ? formData.operational_period_from.split('T')[0] : ''}
                    onChange={(e) => {
                      const date = e.target.value;
                      const time = formData.operational_period_from ? formData.operational_period_from.split('T')[1] : '00:00';
                      setFormData(prev => ({ ...prev, operational_period_from: `${date}T${time}` }));
                    }}
                    className={`bg-gray-800 border-gray-600 text-white`}
                  />
                  <Input
                    type="text"
                    placeholder="HH:MM"
                    pattern="[0-2][0-9]:[0-5][0-9]"
                    maxLength="5"
                    value={formData.operational_period_from ? formData.operational_period_from.split('T')[1] : ''}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^0-9:]/g, '');
                      if (value.length === 2 && !value.includes(':')) {
                        value = value + ':';
                      }
                      if (value.length <= 5) {
                        const date = formData.operational_period_from ? formData.operational_period_from.split('T')[0] : '';
                        setFormData(prev => ({ ...prev, operational_period_from: `${date}T${value}` }));
                      }
                    }}
                    className={`bg-gray-800 border-gray-600 text-white`}
                  />
                </div>
              </div>
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Operational Period To</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="date"
                    value={formData.operational_period_to ? formData.operational_period_to.split('T')[0] : ''}
                    onChange={(e) => {
                      const date = e.target.value;
                      const time = formData.operational_period_to ? formData.operational_period_to.split('T')[1] : '00:00';
                      setFormData(prev => ({ ...prev, operational_period_to: `${date}T${time}` }));
                    }}
                    className={`bg-gray-800 border-gray-600 text-white`}
                  />
                  <Input
                    type="text"
                    placeholder="HH:MM"
                    pattern="[0-2][0-9]:[0-5][0-9]"
                    maxLength="5"
                    value={formData.operational_period_to ? formData.operational_period_to.split('T')[1] : ''}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^0-9:]/g, '');
                      if (value.length === 2 && !value.includes(':')) {
                        value = value + ':';
                      }
                      if (value.length <= 5) {
                        const date = formData.operational_period_to ? formData.operational_period_to.split('T')[0] : '';
                        setFormData(prev => ({ ...prev, operational_period_to: `${date}T${value}` }));
                      }
                    }}
                    className={`bg-gray-800 border-gray-600 text-white`}
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Cost Unit Leader</Label>
                <Input
                  value={formData.cost_unit_leader}
                  onChange={(e) => setFormData(prev => ({ ...prev, cost_unit_leader: e.target.value }))}
                  className={`bg-gray-800 border-gray-600 text-white`}
                />
              </div>
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Contact Information</Label>
                <Input
                  value={formData.contact_info}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact_info: e.target.value }))}
                  placeholder="Email or Phone"
                  className={`bg-gray-800 border-gray-600 text-white`}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Personnel Costs Card */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-orange-500">Personnel Costs</CardTitle>
              <CardDescription className={`${theme.colors.textMuted}`}>
                Track personnel and their associated costs
              </CardDescription>
            </div>
            <div className="flex items-center gap-2 print:hidden">
              {saveStatus === 'saving' && <span className="text-yellow-500 text-sm">Saving...</span>}
              {saveStatus === 'saved' && <span className="text-green-500 text-sm">✓ Saved</span>}
              {saveStatus === 'error' && <span className="text-red-500 text-sm">Save failed</span>}
              <Button onClick={saveEntry} disabled={saving} size="sm" className="bg-green-600 hover:bg-green-700">
                <Save className="h-4 w-4 mr-1" />
                Save
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className={`${theme.colors.border} border-b`}>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Name</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Position</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Agency</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Start Date</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>End Date</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Hours</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Rate ($/hr)</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Total ($)</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2 print:hidden`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.personnel_costs.map((item, index) => (
                    <tr key={item.id} className={`${theme.colors.border} border-b`}>
                      <td className="p-2">
                        <Input
                          value={item.name}
                          onChange={(e) => updatePersonnelCost(index, 'name', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white min-w-[120px]`}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={item.position}
                          onChange={(e) => updatePersonnelCost(index, 'position', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white min-w-[120px]`}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={item.agency}
                          onChange={(e) => updatePersonnelCost(index, 'agency', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white min-w-[120px]`}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          type="date"
                          value={item.start_date}
                          onChange={(e) => updatePersonnelCost(index, 'start_date', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white w-36`}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          type="date"
                          value={item.end_date}
                          onChange={(e) => updatePersonnelCost(index, 'end_date', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white w-36`}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={item.hours_worked}
                          onChange={(e) => updatePersonnelCost(index, 'hours_worked', e.target.value)}
                          placeholder="0"
                          className={`bg-gray-800 border-gray-600 text-white w-20`}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={item.hourly_rate}
                          onChange={(e) => updatePersonnelCost(index, 'hourly_rate', e.target.value)}
                          placeholder="0.00"
                          className={`bg-gray-800 border-gray-600 text-white w-24`}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={item.total_cost}
                          readOnly
                          placeholder="0.00"
                          className={`bg-gray-800 border-gray-600 text-white w-28 bg-gray-100 dark:bg-gray-800`}
                        />
                      </td>
                      <td className="p-2 print:hidden">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => deletePersonnelCost(index)}
                          className="border-red-500 text-red-500 hover:bg-red-500/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className={`${theme.colors.border} border-t-2 bg-gray-100 dark:bg-gray-800`}>
                    <td colSpan="7" className="p-2 text-right font-bold">
                      <span className={`${theme.colors.textPrimary}`}>Personnel Costs Total:</span>
                    </td>
                    <td className="p-2">
                      <span className={`${theme.colors.textPrimary} font-bold text-lg`}>
                        ${formData.total_personnel_cost || '0.00'}
                      </span>
                    </td>
                    <td className="print:hidden"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div className="mt-4 print:hidden">
              <Button onClick={addPersonnelCost} variant="outline" className="border-orange-500 text-orange-500 hover:bg-orange-500/10">
                <Plus className="h-4 w-4 mr-2" />
                Add Personnel
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Equipment Costs Card */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-orange-500">Equipment Costs</CardTitle>
              <CardDescription className={`${theme.colors.textMuted}`}>
                Track equipment and rental costs
              </CardDescription>
            </div>
            <div className="flex items-center gap-2 print:hidden">
              {saveStatus === 'saving' && <span className="text-yellow-500 text-sm">Saving...</span>}
              {saveStatus === 'saved' && <span className="text-green-500 text-sm">✓ Saved</span>}
              {saveStatus === 'error' && <span className="text-red-500 text-sm">Save failed</span>}
              <Button onClick={saveEntry} disabled={saving} size="sm" className="bg-green-600 hover:bg-green-700">
                <Save className="h-4 w-4 mr-1" />
                Save
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className={`${theme.colors.border} border-b`}>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Equipment Name</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Type</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Quantity</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Daily Rate ($)</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Days Used</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Total ($)</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Vendor</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2 print:hidden`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.equipment_costs.map((item, index) => (
                    <tr key={item.id} className={`${theme.colors.border} border-b`}>
                      <td className="p-2">
                        <Input
                          value={item.equipment_name}
                          onChange={(e) => updateEquipmentCost(index, 'equipment_name', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white min-w-[150px]`}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={item.equipment_type}
                          onChange={(e) => updateEquipmentCost(index, 'equipment_type', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white min-w-[120px]`}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={item.quantity}
                          onChange={(e) => updateEquipmentCost(index, 'quantity', e.target.value)}
                          placeholder="0"
                          className={`bg-gray-800 border-gray-600 text-white w-20`}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={item.daily_rate}
                          onChange={(e) => updateEquipmentCost(index, 'daily_rate', e.target.value)}
                          placeholder="0.00"
                          className={`bg-gray-800 border-gray-600 text-white w-24`}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={item.days_used}
                          onChange={(e) => updateEquipmentCost(index, 'days_used', e.target.value)}
                          placeholder="0"
                          className={`bg-gray-800 border-gray-600 text-white w-20`}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={item.total_cost}
                          readOnly
                          placeholder="0.00"
                          className={`bg-gray-800 border-gray-600 text-white w-28 bg-gray-100 dark:bg-gray-800`}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={item.vendor}
                          onChange={(e) => updateEquipmentCost(index, 'vendor', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white min-w-[120px]`}
                        />
                      </td>
                      <td className="p-2 print:hidden">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => deleteEquipmentCost(index)}
                          className="border-red-500 text-red-500 hover:bg-red-500/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className={`${theme.colors.border} border-t-2 bg-gray-100 dark:bg-gray-800`}>
                    <td colSpan="5" className="p-2 text-right font-bold">
                      <span className={`${theme.colors.textPrimary}`}>Equipment Costs Total:</span>
                    </td>
                    <td className="p-2">
                      <span className={`${theme.colors.textPrimary} font-bold text-lg`}>
                        ${formData.total_equipment_cost || '0.00'}
                      </span>
                    </td>
                    <td></td>
                    <td className="print:hidden"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div className="mt-4 print:hidden">
              <Button onClick={addEquipmentCost} variant="outline" className="border-orange-500 text-orange-500 hover:bg-orange-500/10">
                <Plus className="h-4 w-4 mr-2" />
                Add Equipment
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Procurement Contracts Card */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-orange-500">Procurement Contracts</CardTitle>
              <CardDescription className={`${theme.colors.textMuted}`}>
                Manage procurement contracts
              </CardDescription>
            </div>
            <div className="flex items-center gap-2 print:hidden">
              {saveStatus === 'saving' && <span className="text-yellow-500 text-sm">Saving...</span>}
              {saveStatus === 'saved' && <span className="text-green-500 text-sm">✓ Saved</span>}
              {saveStatus === 'error' && <span className="text-red-500 text-sm">Save failed</span>}
              <Button onClick={saveEntry} disabled={saving} size="sm" className="bg-green-600 hover:bg-green-700">
                <Save className="h-4 w-4 mr-1" />
                Save
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className={`${theme.colors.border} border-b`}>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Contract #</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Vendor Name</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Description</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Date</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Status</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Payment Terms</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Total ($)</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2 print:hidden`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.procurement_contracts.map((item, index) => (
                    <tr key={item.id} className={`${theme.colors.border} border-b`}>
                      <td className="p-2">
                        <Input
                          value={item.contract_number}
                          onChange={(e) => updateProcurementContract(index, 'contract_number', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white w-32`}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={item.vendor_name}
                          onChange={(e) => updateProcurementContract(index, 'vendor_name', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white min-w-[150px]`}
                        />
                      </td>
                      <td className="p-2">
                        <Textarea
                          value={item.description}
                          onChange={(e) => updateProcurementContract(index, 'description', e.target.value)}
                          rows={1}
                          className={`bg-gray-800 border-gray-600 text-white min-w-[200px]`}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          type="date"
                          value={item.contract_date}
                          onChange={(e) => updateProcurementContract(index, 'contract_date', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white w-36`}
                        />
                      </td>
                      <td className="p-2">
                        <Select value={item.status} onValueChange={(value) => updateProcurementContract(index, 'status', value)}>
                          <SelectTrigger className={`bg-gray-800 border-gray-600 text-white w-32`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Pending">Pending</SelectItem>
                            <SelectItem value="Active">Active</SelectItem>
                            <SelectItem value="Completed">Completed</SelectItem>
                            <SelectItem value="Cancelled">Cancelled</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-2">
                        <Input
                          value={item.payment_terms}
                          onChange={(e) => updateProcurementContract(index, 'payment_terms', e.target.value)}
                          placeholder="Net 30"
                          className={`bg-gray-800 border-gray-600 text-white min-w-[100px]`}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={item.contract_value}
                          onChange={(e) => updateProcurementContract(index, 'contract_value', e.target.value)}
                          placeholder="0.00"
                          className={`bg-gray-800 border-gray-600 text-white w-28`}
                        />
                      </td>
                      <td className="p-2 print:hidden">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => deleteProcurementContract(index)}
                          className="border-red-500 text-red-500 hover:bg-red-500/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className={`${theme.colors.border} border-t-2 bg-gray-100 dark:bg-gray-800`}>
                    <td colSpan="6" className="p-2 text-right font-bold">
                      <span className={`${theme.colors.textPrimary}`}>Procurement Contracts Total:</span>
                    </td>
                    <td className="p-2">
                      <span className={`${theme.colors.textPrimary} font-bold text-lg`}>
                        ${formData.total_procurement_cost || '0.00'}
                      </span>
                    </td>
                    <td className="print:hidden"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div className="mt-4 print:hidden">
              <Button onClick={addProcurementContract} variant="outline" className="border-orange-500 text-orange-500 hover:bg-orange-500/10">
                <Plus className="h-4 w-4 mr-2" />
                Add Contract
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Incident Costs Card */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-orange-500">Incident-Related Costs</CardTitle>
              <CardDescription className={`${theme.colors.textMuted}`}>
                Track all financial expenditures related to the incident
              </CardDescription>
            </div>
            <div className="flex items-center gap-2 print:hidden">
              {saveStatus === 'saving' && <span className="text-yellow-500 text-sm">Saving...</span>}
              {saveStatus === 'saved' && <span className="text-green-500 text-sm">✓ Saved</span>}
              {saveStatus === 'error' && <span className="text-red-500 text-sm">Save failed</span>}
              <Button onClick={saveEntry} disabled={saving} size="sm" className="bg-green-600 hover:bg-green-700">
                <Save className="h-4 w-4 mr-1" />
                Save
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className={`${theme.colors.border} border-b`}>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Date</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Category</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Description</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Notes</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Total ($)</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2 print:hidden`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.incident_costs.map((item, index) => (
                    <tr key={item.id} className={`${theme.colors.border} border-b`}>
                      <td className="p-2">
                        <Input
                          type="date"
                          value={item.date}
                          onChange={(e) => updateIncidentCost(index, 'date', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white w-36`}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={item.category}
                          onChange={(e) => updateIncidentCost(index, 'category', e.target.value)}
                          placeholder="e.g., Supplies, Services"
                          className={`bg-gray-800 border-gray-600 text-white min-w-[120px]`}
                        />
                      </td>
                      <td className="p-2">
                        <Textarea
                          value={item.description}
                          onChange={(e) => updateIncidentCost(index, 'description', e.target.value)}
                          rows={1}
                          className={`bg-gray-800 border-gray-600 text-white min-w-[200px]`}
                        />
                      </td>
                      <td className="p-2">
                        <Textarea
                          value={item.notes}
                          onChange={(e) => updateIncidentCost(index, 'notes', e.target.value)}
                          rows={1}
                          className={`bg-gray-800 border-gray-600 text-white min-w-[150px]`}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={item.amount}
                          onChange={(e) => updateIncidentCost(index, 'amount', e.target.value)}
                          placeholder="0.00"
                          className={`bg-gray-800 border-gray-600 text-white w-28`}
                        />
                      </td>
                      <td className="p-2 print:hidden">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => deleteIncidentCost(index)}
                          className="border-red-500 text-red-500 hover:bg-red-500/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className={`${theme.colors.border} border-t-2 bg-gray-100 dark:bg-gray-800`}>
                    <td colSpan="4" className="p-2 text-right font-bold">
                      <span className={`${theme.colors.textPrimary}`}>Incident-Related Costs Total:</span>
                    </td>
                    <td className="p-2">
                      <span className={`${theme.colors.textPrimary} font-bold text-lg`}>
                        ${formData.total_incident_cost || '0.00'}
                      </span>
                    </td>
                    <td className="print:hidden"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div className="mt-4 print:hidden">
              <Button onClick={addIncidentCost} variant="outline" className="border-orange-500 text-orange-500 hover:bg-orange-500/10">
                <Plus className="h-4 w-4 mr-2" />
                Add Incident Cost
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Fuel Costs Card */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-orange-500">Fuel Costs</CardTitle>
              <CardDescription className={`${theme.colors.textMuted}`}>
                Track fuel purchases for vehicles, generators, and aircraft
              </CardDescription>
            </div>
            <div className="flex items-center gap-2 print:hidden">
              {saveStatus === 'saving' && <span className="text-yellow-500 text-sm">Saving...</span>}
              {saveStatus === 'saved' && <span className="text-green-500 text-sm">✓ Saved</span>}
              {saveStatus === 'error' && <span className="text-red-500 text-sm">Save failed</span>}
              <Button onClick={saveEntry} disabled={saving} size="sm" className="bg-green-600 hover:bg-green-700">
                <Save className="h-4 w-4 mr-1" />
                Save
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className={`${theme.colors.border} border-b`}>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Date</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Vendor</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Location</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Fuel Type</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Measurement</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Quantity</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Cost ($)</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2 print:hidden`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.fuel_costs.map((item, index) => (
                    <tr key={item.id} className={`${theme.colors.border} border-b`} data-testid={`fuel-cost-row-${index}`}>
                      <td className="p-2">
                        <Input
                          type="date"
                          value={item.date}
                          onChange={(e) => updateFuelCost(index, 'date', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white w-36`}
                          data-testid={`fuel-date-${index}`}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={item.vendor}
                          onChange={(e) => updateFuelCost(index, 'vendor', e.target.value)}
                          placeholder="e.g., Shell"
                          className={`bg-gray-800 border-gray-600 text-white min-w-[140px]`}
                          data-testid={`fuel-vendor-${index}`}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={item.location}
                          onChange={(e) => updateFuelCost(index, 'location', e.target.value)}
                          placeholder="Station / Site"
                          className={`bg-gray-800 border-gray-600 text-white min-w-[140px]`}
                          data-testid={`fuel-location-${index}`}
                        />
                      </td>
                      <td className="p-2">
                        <Select value={item.fuel_type} onValueChange={(value) => updateFuelCost(index, 'fuel_type', value)}>
                          <SelectTrigger className={`bg-gray-800 border-gray-600 text-white w-36`} data-testid={`fuel-type-${index}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Regular">Regular</SelectItem>
                            <SelectItem value="Diesel">Diesel</SelectItem>
                            <SelectItem value="Aviation">Aviation</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-2">
                        <Select value={item.measurement} onValueChange={(value) => updateFuelCost(index, 'measurement', value)}>
                          <SelectTrigger className={`bg-gray-800 border-gray-600 text-white w-32`} data-testid={`fuel-measurement-${index}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="lbs">lbs</SelectItem>
                            <SelectItem value="litres">litres</SelectItem>
                            <SelectItem value="US Gal">US Gal</SelectItem>
                            <SelectItem value="Imp Gal">Imp Gal</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-2">
                        <Input
                          type="number"
                          step="any"
                          value={item.quantity}
                          onChange={(e) => updateFuelCost(index, 'quantity', e.target.value)}
                          placeholder="0"
                          className={`bg-gray-800 border-gray-600 text-white w-24`}
                          data-testid={`fuel-quantity-${index}`}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={item.cost}
                          onChange={(e) => updateFuelCost(index, 'cost', e.target.value)}
                          placeholder="0.00"
                          className={`bg-gray-800 border-gray-600 text-white w-28`}
                          data-testid={`fuel-cost-${index}`}
                        />
                      </td>
                      <td className="p-2 print:hidden">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => deleteFuelCost(index)}
                          className="border-red-500 text-red-500 hover:bg-red-500/10"
                          data-testid={`fuel-delete-${index}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className={`${theme.colors.border} border-t-2 bg-gray-100 dark:bg-gray-800`}>
                    <td colSpan="6" className="p-2 text-right font-bold">
                      <span className={`${theme.colors.textPrimary}`}>Fuel Costs Total:</span>
                    </td>
                    <td className="p-2">
                      <span className={`${theme.colors.textPrimary} font-bold text-lg`} data-testid="fuel-costs-total">
                        ${formData.total_fuel_cost || '0.00'}
                      </span>
                    </td>
                    <td className="print:hidden"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div className="mt-4 print:hidden">
              <Button onClick={addFuelCost} variant="outline" className="border-orange-500 text-orange-500 hover:bg-orange-500/10" data-testid="add-fuel-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Fuel Entry
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Cost Analysis and Summary Card */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-orange-500">Cost Analysis and Summary</CardTitle>
            </div>
            <div className="flex items-center gap-2 print:hidden">
              {saveStatus === 'saving' && <span className="text-yellow-500 text-sm">Saving...</span>}
              {saveStatus === 'saved' && <span className="text-green-500 text-sm">✓ Saved</span>}
              {saveStatus === 'error' && <span className="text-red-500 text-sm">Save failed</span>}
              <Button onClick={saveEntry} disabled={saving} size="sm" className="bg-green-600 hover:bg-green-700">
                <Save className="h-4 w-4 mr-1" />
                Save
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Total Personnel Cost ($)</Label>
                <Input
                  value={formData.total_personnel_cost}
                  readOnly
                  placeholder="0.00"
                  className={`bg-gray-800 border-gray-600 text-white bg-gray-100 dark:bg-gray-800 font-semibold`}
                />
              </div>
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Total Equipment Cost ($)</Label>
                <Input
                  value={formData.total_equipment_cost}
                  readOnly
                  placeholder="0.00"
                  className={`bg-gray-800 border-gray-600 text-white bg-gray-100 dark:bg-gray-800 font-semibold`}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Total Procurement Cost ($)</Label>
                <Input
                  value={formData.total_procurement_cost}
                  readOnly
                  placeholder="0.00"
                  className={`bg-gray-800 border-gray-600 text-white bg-gray-100 dark:bg-gray-800 font-semibold`}
                />
              </div>
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Total Incident Cost ($)</Label>
                <Input
                  value={formData.total_incident_cost}
                  readOnly
                  placeholder="0.00"
                  className={`bg-gray-800 border-gray-600 text-white bg-gray-100 dark:bg-gray-800 font-semibold`}
                />
              </div>
            </div>
            <div>
              <Label className={`text-white text-xs font-medium mb-1 block`}>Total Fuel Cost ($)</Label>
              <Input
                value={formData.total_fuel_cost}
                readOnly
                placeholder="0.00"
                className={`bg-gray-800 border-gray-600 text-white bg-gray-100 dark:bg-gray-800 font-semibold`}
                data-testid="summary-total-fuel-cost"
              />
            </div>
            <div>
              <Label className={`text-white text-xs font-medium mb-1 block font-bold`}>Grand Total Cost ($)</Label>
              <Input
                value={formData.grand_total_cost}
                readOnly
                placeholder="0.00"
                className={`bg-gray-800 border-gray-600 text-white font-bold text-lg bg-yellow-100 dark:bg-yellow-900`}
              />
            </div>
            <div>
              <Label className={`text-white text-xs font-medium mb-1 block`}>Cost Estimate Notes</Label>
              <Textarea
                value={formData.cost_estimate_notes}
                onChange={(e) => setFormData(prev => ({ ...prev, cost_estimate_notes: e.target.value }))}
                rows={3}
                placeholder="Notes on cost estimates..."
                className={`bg-gray-800 border-gray-600 text-white`}
              />
            </div>
            <div>
              <Label className={`text-white text-xs font-medium mb-1 block`}>Potential Savings Notes</Label>
              <Textarea
                value={formData.potential_savings_notes}
                onChange={(e) => setFormData(prev => ({ ...prev, potential_savings_notes: e.target.value }))}
                rows={3}
                placeholder="Analysis of potential cost savings..."
                className={`bg-gray-800 border-gray-600 text-white`}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Prepared By</Label>
                <Input
                  value={formData.prepared_by}
                  onChange={(e) => setFormData(prev => ({ ...prev, prepared_by: e.target.value }))}
                  className={`bg-gray-800 border-gray-600 text-white`}
                />
              </div>
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Date Prepared</Label>
                <Input
                  type="date"
                  value={formData.date_prepared}
                  onChange={(e) => setFormData(prev => ({ ...prev, date_prepared: e.target.value }))}
                  className={`bg-gray-800 border-gray-600 text-white`}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end print:hidden">
          <Button onClick={saveEntry} disabled={loading} className="bg-orange-500 hover:bg-orange-600">
            <Save className="h-4 w-4 mr-2" />
            {loading ? 'Saving...' : (editingEntry ? 'Update' : 'Save')} Entry
          </Button>
        </div>
      </div>
    </div>
  );
};

// Image Repository Component

export default CostUnitForm;
